/**
 * UI preferences + profile + workspace memory endpoints.
 */

import { ApiError, extractError, get } from './_shared.ts';

// ---------------------------------------------------------------------
// UI preferences (D-089)
// ---------------------------------------------------------------------

export type ThemeChoice = 'light' | 'dark' | 'system';

export interface UiPreferences {
  /** 'list' | 'widget' default 'widget' */
  connectors_view?: 'list' | 'widget';
  /** 'list' | 'widget' default 'widget' */
  people_view?: 'list' | 'widget';
  /** 'list' | 'widget' default 'widget' */
  catalog_view?: 'list' | 'widget';
  /** Theme preference; 'system' tracks the OS-level prefers-color-scheme. */
  theme?: ThemeChoice;
  /**
   * D-089 + 2026-06-03 UX reset #12: site-wide layout density. One preference
   * applies to every list page. 'widget' is the default because demos look
   * cooler. Power users flip to 'list'.
   */
  layout?: 'widget' | 'list';
  /**
   * @deprecated Pre-smoke close (2026-07-23) — power-user toggle retired.
   * Chat model picker no longer offers a "Show technical names" affordance.
   * Field preserved on the interface so the /api/me/preferences PUT contract
   * silently drops legacy JSONB keys without failing older SPA builds; new
   * code MUST NOT set this key. Existing JSONB entries are load-bearing only
   * for backward-compat with old client payloads and are ignored on read.
   */
  chat_show_technical_model_names?: boolean;
  /**
   * Push #84 round 5 (Ean dogfood 2026-06-19) — chat settings moved
   * from localStorage to per-user server prefs so Lori's customizations
   * follow her across browsers/devices and don't bleed into her
   * teammates' configurations. Keys mirror the localStorage flags the
   * Chat surface used previously (`chat:show-reasoning`, etc.) so the
   * migration is a one-time hydrate. All default to false except
   * autoScroll (default true — staying pinned to latest is the
   * expected chat behavior).
   */
  /** Reveal the model's reasoning blocks when the model supports them. */
  chat_show_reasoning?: boolean;
  /** Reveal raw tool responses under each step (default off — operator/power-user feature). */
  chat_show_technical_details?: boolean;
  /** Append "be terse" instructions to the system prompt — fewer tokens. */
  chat_compact_responses?: boolean;
  /** Pin the stream to the bottom as tokens arrive. Default true. */
  chat_auto_scroll?: boolean;
  /** Render per-turn + session cost in the composer footer. */
  chat_show_cost?: boolean;
  /**
   * Push #84 round 14 (Ean dogfood 2026-06-21) — when ON, the
   * composer's web-search chip auto-enables every time the user
   * switches to a model that supports web_search. Eliminates the
   * "have to toggle on every turn" friction. Olytic still asks once
   * per session before the first live-web query (the "ask before
   * querying the internet" guard), so the user has explicit consent
   * without per-turn ceremony.
   *
   * Default off — the safer "I'll opt in deliberately" stance. Users
   * who lean on live web context (sales, marketing, research roles)
   * flip this on once + forget about it.
   */
  chat_web_search_default?: boolean;

  /**
   * Push 5-D Item 6 (2026-07-11) — retry transient dispatch failures.
   * When ON, /api/chat/stream retries Anthropic 429/5xx/529 + network
   * failures with exponential backoff (500ms + 1500ms; max 2 retries).
   * Default ON (reliability > speed on the failure path). Pref is
   * surfaced today for UX transparency; server retry is unconditional
   * via the fetchWithRetry helper. Future push honors the pref for
   * users who explicitly opt out.
   */
  chat_retry_failed_dispatches?: boolean;
  /**
   * Push 5-D Item 6 (2026-07-11) — extended thinking default. When ON
   * AND the active model advertises `extended_thinking` (Migration
   * 0148 seeded Claude Sonnet/Opus 4.6), the composer's Thinking chip
   * pre-flips on when a new chat opens. Default OFF — extended
   * thinking is slower + more expensive, opt-in only.
   */
  chat_extended_thinking_default?: boolean;

  // ---------------------------------------------------------------------
  // Push #88 notification preferences (2026-06-22)
  //
  // Per @olytic/notifications NotificationEvent + NotificationPreference.
  // Each event has a {bell, email} pair — bell controls sys__notification
  // row writes, email controls sendEmail() invocations. Defaults live in
  // NOTIFICATION_DEFAULTS (admin events: bell+email on; user events: bell
  // on, email off). Missing keys fall through to defaults.
  //
  // D-182 classification: features (per-user variance) → ui_preferences
  // JSONB. No new table, no migration. Adding a new event = one entry
  // in @olytic/notifications/types.ts + one optional key here.
  // ---------------------------------------------------------------------

  /** Admin-scope: someone hits the Help → Contact Olytic flow on this account. */
  notify_user_help_request?: { bell?: boolean; email?: boolean };
  /** Admin-scope: a connector needs OAuth reauthorization. */
  notify_oauth_reauth_needed?: { bell?: boolean; email?: boolean };
  /** Admin-scope: same tool/connector keeps failing dispatch. */
  notify_repeated_dispatch_failures?: { bell?: boolean; email?: boolean };
  /** Admin-scope: the workspace credit pool drops below threshold. */
  notify_low_credits_warning?: { bell?: boolean; email?: boolean };
  /** Admin-scope: a teammate hit a high-severity error and their toast said
   *  "your admin has been notified." Push 4-1ai (2026-07-10). */
  notify_user_error_reported?: { bell?: boolean; email?: boolean };

  /** User-scope: an admin approves or denies the user's credit request. */
  notify_my_request_decided?: { bell?: boolean; email?: boolean };
  /** User-scope: an admin adds/removes the user from a permission set or assignment. */
  notify_my_assignment_changed?: { bell?: boolean; email?: boolean };
  /** User-scope: the user's connector OAuth was revoked or reconnected. */
  notify_my_connector_status?: { bell?: boolean; email?: boolean };
}

export function fetchPreferences(): Promise<{ ui_preferences: UiPreferences }> {
  return get('/api/me/preferences');
}

export function updatePreferences(
  patch: UiPreferences,
): Promise<{ ok: true; ui_preferences: UiPreferences }> {
  return fetch('/api/me/preferences', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(async (res) => {
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text === '' ? null : JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === 'object' && parsed !== null && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `PUT /api/me/preferences failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true; ui_preferences: UiPreferences };
  });
}

// ---------------------------------------------------------------------
// Profile (D-098 + Push #4.5 Item 4)
//
// Editable fields:
//   - display_name (D-098, always editable on self)
//   - confirm_writes_in_chat (Migration 0116, per-user UX preference for the
//     ConfirmCard architecture; only affects Olytic chat, not external MCP)
// ---------------------------------------------------------------------

/**
 * Round 6.5 Item 26 — extended editable surface. timezone + per-kind notif
 * partial-merge land here. Theme/layout/view-density continue to ride
 * /api/me/preferences (which is the free-shape SPA-as-canon writer).
 */
export type ProfileNotifPrefKey =
  | 'notify_credit_threshold'
  | 'notify_user_help_request'
  | 'notify_purchase_receipt'
  | 'notify_low_credits'
  | 'notify_approval_decision'
  | 'notify_capability_assigned'
  | 'notify_chat_shared_with_me'
  | 'notify_weekly_digest';

export function updateProfile(
  patch: {
    display_name?: string;
    confirm_writes_in_chat?: boolean;
    timezone?: string;
    ui_preferences_patch?: Partial<Record<ProfileNotifPrefKey, boolean>>;
    /**
     * Push 4-1j — per-user opt-out for inbound sharing (Migration 0104,
     * D-187). When false, no share fn can target this user (share fns
     * raise ERRCODE 42501). Read half already lives on /api/me/teammates
     * to grey out ineligible share targets in RecipientPickerModal.
     */
    allow_others_to_share_with_me?: boolean;
  },
): Promise<{
  ok: true;
  display_name?: string;
  confirm_writes_in_chat?: boolean;
  timezone?: string;
  ui_preferences?: Record<string, unknown>;
  allow_others_to_share_with_me?: boolean;
}> {
  return fetch('/api/me/profile', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(handlePatchResponseLocal) as Promise<{
    ok: true;
    display_name?: string;
    confirm_writes_in_chat?: boolean;
    timezone?: string;
    ui_preferences?: Record<string, unknown>;
    allow_others_to_share_with_me?: boolean;
  }>;
}

// Local copy of handlePatchResponse — kept here so this module doesn't need
// to re-export _shared's helper for an internal use.
async function handlePatchResponseLocal(res: Response): Promise<unknown> {
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text === '' ? null : JSON.parse(text);
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const msg =
      parsed && typeof parsed === 'object' && parsed !== null && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : `PATCH failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return parsed;
}

// ---- Workspace memory (Push #24) ----

export interface WorkspaceMemory {
  memory: string;
  max_chars: number;
}

export function fetchWorkspaceMemory(): Promise<WorkspaceMemory> {
  return get('/api/admin/workspace-memory');
}

export async function saveWorkspaceMemory(memory: string): Promise<{ ok: true; memory: string }> {
  const res = await fetch('/api/admin/workspace-memory', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ memory }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { message, detail, incidentId, code } = extractError(text, '/api/admin/workspace-memory', 'PUT', res.status);
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return (await res.json()) as { ok: true; memory: string };
}
